// @flow
import type {Navigation} from '../../../../../data/navigation/Navigation';

export class Page {
  title: string;

  hover: string;

  product: string;

  route: string;

  slug: string;

  children: Array<Page>;

  parent: Page | null;

  constructor(data: Object) {
    this.title = data.title || '';
    this.hover = data.hover;
    this.product = data.product;
    this.route = data.route;
    this.children = this.buildChildren(data.children);
    this.slug = data.slug;
  }

  buildChildren(children: Array<Object>): Array<Page> {
    const newChildren = [];
    children.forEach((child) => {
      const page = new Page(child);
      page.parent = this;
      newChildren.push(page);
    });
    return newChildren;
  }
}

export const Pages = (navigation: Navigation): Page => {
  const rootChildren = [];
  for (const topic of navigation.topics) {
    if (topic.text !== 'Home' && topic.xray?.conditions === undefined) {
      const topicChildren = [];
      for (const section of topic.sections) {
        const product = navigation.products.filter(
          (p) => p.text === section,
        )[0];
        topicChildren.push(getChildPage(product.text, product));
      }
      rootChildren.push(
        new Page({
          title: topic.text,
          route: topic.href,
          slug: slug(topic.text),
          product: null,
          hover: null,
          children: topicChildren,
        }),
      );
    }
  }
  const data = new Page({
    title: 'Home',
    route: '/docs',
    slug: 'home',
    product: null,
    hover: null,
    children: rootChildren,
  });
  return data;
};

function getChildPage(product: String, node: any): Page {
  if (node.children.length === 0) {
    return new Page({
      product,
      title: node.text,
      route: node.href,
      slug: slug(node.text),
      hover: node.description ? node.description : node.text, // NavigationProduct does not have description
      children: [],
    });
  }
  const children = [];
  for (const child of node.children) {
    if (!!child.href && child.xray?.conditions === undefined) {
      children.push(getChildPage(product, child));
    }
  }
  return new Page({
    product,
    children,
    title: node.text,
    route: node.href ? node.href : node.children[0].href,
    slug: slug(node.text),
    hover: node.description ? node.description : node.text, // NavigationProduct does not have description
  });
}

// Find a specific page based on the given path
export function search(path: string, page: Page): Page | null {
  // terminal state, page and route match
  if (path === page.route) {
    return page;
  }

  // assume no match
  let newPage = null;

  // recursively traverse all child pages
  for (const child of page.children) {
    const searchResult = search(path, child);

    // only update the new page if the result from the search is not
    // null. if we find a page we want to preserve the match
    if (searchResult !== null) {
      newPage = searchResult;
    }
  }

  // if a match is never found this will return null
  return newPage;
}

// entry function for parsing a path to a specific page. in this case
// the path split by slashes as if they were folder paths to mimic
// the filesystem behavior.
export function parsePath(
  path: string,
  page: Page,
  navigation: Navigation,
): Page | null {
  const parts = path.split('/');
  const newPage = _parsePath(parts, page, navigation);
  return newPage;
}

// walk the page tree based on behavior for file system directory
// navigation. this will return the matched page or null if the path
// is not a valid part of the page tree.
function _parsePath(
  parts: Array<string>,
  page: Page,
  navigation: Navigation,
): Page | null {
  // terminal state, return matched page
  if (parts.length === 0) {
    return page;
  }

  // pop the next step we need to take and remove it from the rest of
  // the series
  const part = parts.shift();

  // basically do nothing other than pop the part, this is the
  // current page
  if (part === '.') {
    return _parsePath(parts, page, navigation);
  }

  // navigate up a page if there is a parent. if not we've likely
  // reached the root so keep that.
  if (part === '..') {
    if (page.parent) {
      return _parsePath(parts, page.parent, navigation);
    }

    return _parsePath(parts, page, navigation);
  }

  // If it starts with a ~, start from root
  if (part === '~') {
    return _parsePath(parts, Pages(navigation), navigation);
  }

  // search each child of the current page to see if there is a match
  // if there is, navigate to it. if not, do nothing
  for (const child of page.children) {
    // the slug comparison makes navigating between pages a little
    // easier by letting the user specify lower case and dashes
    if (slug(part) === slug(child.title) || part === child.slug) {
      return _parsePath(parts, child, navigation);
    }
  }

  // no matches found
  return null;
}

function slug(str: string): string {
  return str.toLowerCase().replace(/ /g, '-');
}

export function relativePath(page: Page): string {
  const path = [];

  let current = page;
  do {
    path.unshift(current.slug);
    current = current.parent;
  } while (current && current.parent !== null);

  // We can't navigate directly from `/home`, so encourage using ~
  return `/${path.join('/')}`.replace('/home', '~');
}
